import { useState } from 'react';
import {
  Form,
  InputNumber,
  Button,
  Card,
  Table,
  Typography,
  Alert,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { isAxiosError } from 'axios';
import { useCreateShipment, useFefoPreview } from '@hooks/useWarehouse';
import ProductSearchSelect from '@components/ProductSearchSelect';
import type { BatchAllocation, ShipmentResult } from '@/types/warehouse';

const { Title, Text } = Typography;

interface ShipmentFormValues {
  productId: string;
  quantity: number;
}

const mdAllocationColumns: ColumnsType<BatchAllocation> = [
  {
    title: 'Партія',
    dataIndex: 'batchNumber',
    key: 'batchNumber',
  },
  {
    title: 'Термін придатності',
    dataIndex: 'expiryDate',
    key: 'expiryDate',
    render: (mdValue: string | null) => (mdValue ? dayjs(mdValue).format('DD.MM.YYYY') : '—'),
  },
  {
    title: 'Кількість до відвантаження',
    dataIndex: 'allocatedQuantity',
    key: 'allocatedQuantity',
    align: 'right',
  },
];

export default function ShipmentForm() {
  const [mdForm] = Form.useForm<ShipmentFormValues>();
  const mdCreateShipment = useCreateShipment();

  const [mdSelectedProductId, setMdSelectedProductId] = useState<string | undefined>();
  const [mdQuantity, setMdQuantity] = useState<number | undefined>();
  const [mdLastResult, setMdLastResult] = useState<ShipmentResult | null>(null);

  // Попередній FEFO-розподіл необов'язковий: без нього сервер підбере партії сам при відвантаженні
  const {
    data: mdAllocations,
    isLoading: mdPreviewLoading,
    isError: mdPreviewError,
    error: mdPreviewErrorValue,
  } = useFefoPreview(mdSelectedProductId, mdQuantity);
  // 400 — сервер порахував розподіл і придатного залишку не вистачає; решта — сервіс недоступний
  const mdPreviewShortage = isAxiosError(mdPreviewErrorValue) && mdPreviewErrorValue.response?.status === 400;

  const handleProductChange = (mdValue: string) => {
    setMdSelectedProductId(mdValue);
    setMdQuantity(undefined);
    mdForm.setFieldValue('quantity', undefined);
    setMdLastResult(null);
  };

  const handleQuantityChange = (mdValue: number | null) => {
    setMdQuantity(mdValue ?? undefined);
    setMdLastResult(null);
  };

  const handleSubmit = async (mdValues: ShipmentFormValues) => {
    const mdResult = await mdCreateShipment.mutateAsync({
      productId: mdValues.productId,
      quantity: mdValues.quantity,
    });
    mdForm.resetFields();
    setMdSelectedProductId(undefined);
    setMdQuantity(undefined);
    setMdLastResult(mdResult);
  };

  const mdTotalAllocated = (mdAllocations ?? []).reduce(
    (mdSum, mdA) => mdSum + mdA.allocatedQuantity,
    0
  );

  return (
    <Card style={{ maxWidth: 700 }}>
      <Title level={5} style={{ marginBottom: 24 }}>
        Відвантаження (FEFO)
      </Title>

      <Form
        form={mdForm}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
      >
        <Form.Item
          label="Продукт"
          name="productId"
          rules={[{ required: true, message: 'Оберіть продукт' }]}
        >
          <ProductSearchSelect placeholder="Пошук продукту..." onChange={handleProductChange} />
        </Form.Item>

        <Form.Item
          label="Кількість"
          name="quantity"
          rules={[{ required: true, message: 'Вкажіть кількість' }]}
        >
          <InputNumber
            min={1}
            placeholder="Кількість до відвантаження"
            style={{ width: '100%' }}
            onChange={handleQuantityChange}
          />
        </Form.Item>

        {mdPreviewError && (
          <Alert
            message={
              mdPreviewShortage
                ? 'Недостатньо придатного залишку для цієї кількості. Партії з терміном придатності менше 90 днів, заблоковані та зарезервовані не відвантажуються.'
                : 'Попередній FEFO-розподіл недоступний — партії буде підібрано автоматично під час відвантаження'
            }
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {mdAllocations && mdAllocations.length > 0 && (
          <>
            <Alert
              message={`FEFO розподіл: ${mdTotalAllocated} од. з ${mdAllocations.length} партій`}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Table
              columns={mdAllocationColumns}
              dataSource={mdAllocations}
              rowKey="batchId"
              size="small"
              pagination={false}
              loading={mdPreviewLoading}
              style={{ marginBottom: 24 }}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={2}>
                    <Text strong>Всього</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <Text strong>{mdTotalAllocated}</Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </>
        )}

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={mdCreateShipment.isPending}
            size="large"
          >
            Підтвердити відвантаження
          </Button>
        </Form.Item>
      </Form>

      {mdLastResult && (
        <>
          <Alert
            message={`Відвантажено ${mdLastResult.totalShipped} од. з ${mdLastResult.allocations.length} партій`}
            description={
              mdLastResult.warnings.length > 0 ? mdLastResult.warnings.join('; ') : undefined
            }
            type={mdLastResult.warnings.length > 0 ? 'warning' : 'success'}
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Table
            columns={mdAllocationColumns}
            dataSource={mdLastResult.allocations}
            rowKey="batchId"
            size="small"
            pagination={false}
          />
        </>
      )}
    </Card>
  );
}
